// M06 Übersicht.
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDashboard, useMe } from '../api/queries';
import { Icon } from '../components/Icon';
import { formatBytes, formatDateTime, PageHead, relativeTime, Spinner } from '../components/ui';
import { localeOf } from '../i18n';
import { QuickstartCard } from './templates/Quickstart';

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data: d, isLoading } = useDashboard();
  const first = me?.user.displayName?.split(/[\s@]/)[0];
  const tenantName = me?.memberships.find((m) => m.tenantId === me.activeTenantId)?.tenantName;
  if (isLoading || !d) return <><PageHead title={t('dashboard.hello', { name: first })} /><Spinner /></>;

  const stat = (to: string, icon: string, cls: string, v: number, label: string) => (
    <Link className="card stat" to={to}>
      <div className={`ic ${cls}`}><Icon name={icon} /></div>
      <div><div className="v">{v}</div><div className="muted">{label}</div></div>
    </Link>
  );
  const emptyTenant = d.devices.total === 0 && d.counts.media === 0 && d.counts.playlists === 0;
  const pct = d.storage.quotaBytes ? Math.min(100, (d.storage.usedBytes / d.storage.quotaBytes) * 100) : 0;

  return (
    <>
      <PageHead title={first ? t('dashboard.hello', { name: first }) : t('nav.dashboard')} sub={tenantName} />
      <div className="stats">
        {stat('/screens', 'screen', '', d.devices.total, t('dashboard.total'))}
        {stat('/screens?status=online', 'check', 'ok', d.devices.online, t('dashboard.online'))}
        {stat('/screens?status=offline', 'x', 'off', d.devices.offline, t('dashboard.offline'))}
        {stat('/screens', 'alert', 'err', d.devices.error, t('dashboard.error'))}
      </div>
      {d.counts.playlists === 0 && <QuickstartCard />}
      {emptyTenant && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2>{t('dashboard.start')}</h2>
          <ol className="checklist">
            <li className={d.devices.total ? 'done' : ''}><Link to="/screens/add">{t('dashboard.step1')}</Link></li>
            <li className={d.counts.media ? 'done' : ''}><Link to="/media">{t('dashboard.step2')}</Link></li>
            <li className={d.counts.playlists ? 'done' : ''}><Link to="/playlists">{t('dashboard.step3')}</Link></li>
            <li className={d.recentPublications.length ? 'done' : ''}><span>{t('dashboard.step4')}</span></li>
          </ol>
        </div>
      )}
      <div className="grid-2">
        <div className="card">
          <h2>{t('dashboard.attention')}</h2>
          {d.attention.length ? (
            <ul className="list-plain">
              {d.attention.map((a, i) => (
                <li key={i}>
                  <span className={`pill ${a.kind === 'offline' ? 'offline' : a.kind === 'error' || a.kind === 'media_rejected' ? 'error' : 'warn'}`}>{t(`attention.${a.kind}`)}</span>
                  {a.targetId ? <Link className="grow" to={a.kind === 'media_rejected' ? '/media' : `/screens/${a.targetId}`}>{a.name}</Link> : <span className="grow">{a.name}</span>}
                  <span className="muted small">{a.since ? relativeTime(a.since, locale) : ''}</span>
                </li>
              ))}
            </ul>
          ) : <div className="muted">{t('dashboard.allGood')}</div>}
        </div>
        <div className="card">
          <h2>{t('dashboard.recent')}</h2>
          {d.recentPublications.length ? (
            <ul className="list-plain">
              {d.recentPublications.map((p) => (
                <li key={`${p.playlistId}-${p.revisionNo}`} className="split">
                  <Link to={`/playlists/${p.playlistId}`}><b>{p.playlistName}</b></Link>
                  <span className="muted small">{t('playlists.revision', { no: p.revisionNo })} · {p.publishedBy} · {formatDateTime(p.publishedAt, locale)}</span>
                </li>
              ))}
            </ul>
          ) : <div className="muted">{t('dashboard.noPublications')}</div>}
        </div>
        <div className="card">
          <h2>{t('dashboard.storage')}</h2>
          <div className="bar" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}><div style={{ width: `${pct}%` }} /></div>
          <div className="muted small" style={{ marginTop: 8 }}>{t('dashboard.storageOf', { used: formatBytes(d.storage.usedBytes, locale), quota: formatBytes(d.storage.quotaBytes, locale) })}</div>
          <div className="row small muted" style={{ marginTop: 14, gap: 18 }}>
            <Link to="/playlists">{t('dashboard.countPlaylists', { count: d.counts.playlists })}</Link>
            <Link to="/media">{t('dashboard.countMedia', { count: d.counts.media })}</Link>
            <Link to="/locations">{t('dashboard.countLocations', { count: d.counts.locations })}</Link>
            <Link to="/schedules">{t('dashboard.countSchedules', { count: d.counts.schedules })}</Link>
          </div>
        </div>
      </div>
    </>
  );
}
